import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { CalendarOutlined, MessageOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import {
  formatDateTime,
  formatMeetingRange,
  browserTimeZone,
} from '../../../shared/format/entity-detail.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import type { MeetingItem } from '../../meetings/api/meetings.client.js';
import type { ContactInteractionItem } from '../api/contacts.client.js';
export function ContactOverview({
  description,
  tasks,
  meetings,
  interactions,
}: {
  description?: string | null;
  tasks: TaskItem[];
  meetings: MeetingItem[];
  interactions: ContactInteractionItem[];
}): ReactElement {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const taskParams = new URLSearchParams(params);
  taskParams.set('tab', 'tasks');
  const now = Date.now();
  const openTasks = tasks
    .filter((item) => item.status !== 'done' && item.status !== 'cancelled' && item.dueAt)
    .sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!));
  const overdue = openTasks.filter((item) => Date.parse(item.dueAt!) < now);
  const task = openTasks.find((item) => Date.parse(item.dueAt!) >= now);
  const meeting = meetings
    .filter((item) => item.status === 'scheduled' && Date.parse(item.startsAt) >= now)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  if (!description && !task && !overdue.length && !meeting && !interactions.length)
    return (
      <section className="entity-panel">
        <EmptyState text={t('crm.entity_detail.empty_overview')} />
      </section>
    );
  return (
    <div className="entity-overview">
      {description ? (
        <section className="entity-panel">
          <h3>{t('crm.entity_detail.description')}</h3>
          <p className="entity-note-text">{description}</p>
        </section>
      ) : null}
      {overdue.length ? (
        <section className="entity-panel">
          <div className="entity-list-toolbar">
            <h3>{t('crm.entity_detail.overdue_tasks', { count: overdue.length })}</h3>
            <Link data-testid="contact-overview-all-tasks" to={`?${taskParams.toString()}`}>
              {t('crm.entity_detail.all_tasks')}
            </Link>
          </div>
          {overdue.slice(0, 3).map((item) => (
            <div key={item.id} className="entity-overview-action">
              <Link to={`/tasks/${item.id}`}>{item.title}</Link>
              <EntityTaskDueChip dueAt={item.dueAt ?? null} />
            </div>
          ))}
        </section>
      ) : null}
      {task ? (
        <section className="entity-panel">
          <h3>{t('crm.entity_detail.next_task')}</h3>
          <div className="entity-overview-action">
            <Link to={`/tasks/${task.id}`}>{task.title}</Link>
            <EntityTaskDueChip dueAt={task.dueAt ?? null} />
          </div>
        </section>
      ) : null}
      {meeting ? (
        <section className="entity-panel">
          <h3>{t('crm.entity_detail.next_meeting')}</h3>
          <div className="entity-overview-action entity-overview-meeting">
            <Link to={`/meetings/${meeting.id}`}>{meeting.title}</Link>
            <Tooltip
              title={
                meeting.timezone !== browserTimeZone()
                  ? formatDateTime(meeting.startsAt, meeting.timezone)
                  : undefined
              }
            >
              <span className="entity-meta">
                {formatMeetingRange(meeting.startsAt, meeting.endsAt)}
              </span>
            </Tooltip>
          </div>
        </section>
      ) : null}
      {interactions.length ? (
        <section className="entity-panel">
          <h3>{t('crm.entity_detail.recent_history')}</h3>
          <EntityHistoryFeed
            items={[...interactions]
              .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt))
              .slice(0, 3)
              .map((item) => ({
                id: item.id,
                date: item.occurredAt,
                icon:
                  item.type === 'call' ? (
                    <PhoneOutlined />
                  ) : item.type === 'meeting' ? (
                    <CalendarOutlined />
                  ) : (
                    <MessageOutlined />
                  ),
                meta: [item.channel, item.subject].filter(Boolean).join(' · '),
                text: (
                  <>
                    <strong>{t(`crm.entity_detail.interaction_${item.type}`)}</strong>
                    {item.body ? <span> · {item.body}</span> : null}
                  </>
                ),
              }))}
          />
        </section>
      ) : null}
    </div>
  );
}
